import React, {useState, useEffect} from 'react';
import {Button, Chip, CircularProgress, Typography} from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import {ServiceLocator} from "../../../Services/ServiceLocator";

/**
 * Bulk-assign one or more SEMA/VCdb vehicles to many selected products at once.
 * `products` is an array of { productType, productId }.
 */
function BulkAssignVehiclesDrawer({open, onClose, products, onComplete}) {
    const vehicleService = ServiceLocator.Instance.getVehicleService();

    const [years, setYears] = useState([]);
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedMake, setSelectedMake] = useState(null);
    const [selectedModel, setSelectedModel] = useState(null);
    const [staged, setStaged] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [semaUnavailable, setSemaUnavailable] = useState(false);

    useEffect(() => {
        if (!open) return;
        setStaged([]);
        setSelectedYear(null);
        setSelectedMake(null);
        setSelectedModel(null);
        setError('');
        vehicleService.getYears()
            .then((list) => {
                setYears(Array.isArray(list) ? list : []);
                setSemaUnavailable(false);
            })
            .catch(() => setSemaUnavailable(true));
    }, [open, vehicleService]);

    useEffect(() => {
        setSelectedMake(null);
        setSelectedModel(null);
        setModels([]);
        if (!selectedYear) {
            setMakes([]);
            return;
        }
        vehicleService.getMakes(selectedYear)
            .then((list) => setMakes((list || []).map((m) => m.makeName)))
            .catch(() => setSemaUnavailable(true));
    }, [selectedYear, vehicleService]);

    useEffect(() => {
        setSelectedModel(null);
        if (!selectedYear || !selectedMake) {
            setModels([]);
            return;
        }
        setLoadingModels(true);
        vehicleService.getModels(selectedYear, selectedMake)
            .then((list) => setModels(Array.isArray(list) ? list : []))
            .catch(() => setSemaUnavailable(true))
            .finally(() => setLoadingModels(false));
    }, [selectedYear, selectedMake, vehicleService]);

    const handleStage = () => {
        if (!selectedYear || !selectedMake || !selectedModel) return;
        const entry = {
            baseVehicleId: selectedModel.baseVehicleId,
            year: selectedYear,
            makeName: selectedMake,
            modelName: selectedModel.modelName,
        };
        setStaged((prev) => prev.some((v) => v.baseVehicleId === entry.baseVehicleId) ? prev : [...prev, entry]);
        setSelectedModel(null);
    };

    const handleUnstage = (baseVehicleId) => {
        setStaged((prev) => prev.filter((v) => v.baseVehicleId !== baseVehicleId));
    };

    const handleAssign = () => {
        if (staged.length === 0 || !products || products.length === 0) return;
        setSaving(true);
        setError('');
        vehicleService.bulkAddFitments(products, staged)
            .then((result) => {
                if (window.$alertSuccess) {
                    window.$alertSuccess(`Assigned ${staged.length} vehicle(s) to ${products.length} product(s) (${(result && result.added) || 0} new links).`);
                }
                if (onComplete) onComplete();
                if (onClose) onClose();
            })
            .catch((err) => setError((err && err.message) || 'Failed to assign vehicles.'))
            .finally(() => setSaving(false));
    };

    const productCount = products ? products.length : 0;

    return (
        <DrawerMenuRight
            title="Assign Vehicles"
            customWidth={600}
            open={open}
            onClose={onClose}
            disableEnterKeySubmit
            hasUnsavedChanges={staged.length > 0}
        >
            <div style={{padding: '20px'}}>
                <Typography variant="body2" style={{color: '#666', marginBottom: 16}}>
                    Add one or more vehicles (Year / Make / Model), then assign them to all {productCount} selected product(s).
                    Uses SEMA / ACA VCdb data.
                </Typography>

                {semaUnavailable && (
                    <Typography variant="body2" style={{color: '#c00', marginBottom: 12}}>
                        SEMA vehicle lookups are unavailable (check SEMA Data subscription credentials in the API config).
                    </Typography>
                )}

                <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16}}>
                    <Autocomplete
                        options={years}
                        getOptionLabel={(o) => String(o)}
                        value={selectedYear}
                        onChange={(e, v) => setSelectedYear(v)}
                        style={{width: 120}}
                        disabled={semaUnavailable || years.length === 0}
                        renderInput={(params) => <TextField {...params} label="Year" variant="outlined" size="small"/>}
                    />
                    <Autocomplete
                        options={makes}
                        getOptionLabel={(o) => o}
                        value={selectedMake}
                        onChange={(e, v) => setSelectedMake(v)}
                        style={{width: 180}}
                        disabled={!selectedYear || makes.length === 0}
                        renderInput={(params) => <TextField {...params} label="Make" variant="outlined" size="small"/>}
                    />
                    <Autocomplete
                        options={models}
                        getOptionLabel={(o) => o.modelName}
                        getOptionSelected={(o, v) => o.baseVehicleId === v.baseVehicleId}
                        value={selectedModel}
                        onChange={(e, v) => setSelectedModel(v)}
                        style={{width: 200}}
                        loading={loadingModels}
                        disabled={!selectedMake || models.length === 0}
                        renderInput={(params) => <TextField {...params} label="Model" variant="outlined" size="small"/>}
                    />
                    <Button variant="outlined" color="primary" onClick={handleStage} disabled={!selectedModel}>
                        Add
                    </Button>
                </div>

                {staged.length > 0 && (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16}}>
                        {staged.map((v) => (
                            <Chip
                                key={v.baseVehicleId}
                                label={`${v.year} ${v.makeName} ${v.modelName}`}
                                onDelete={() => handleUnstage(v.baseVehicleId)}
                            />
                        ))}
                    </div>
                )}

                {error && <Typography variant="body2" style={{color: '#c00', marginBottom: 12}}>{error}</Typography>}

                <div style={{marginBottom: 16}}>Selected products: {productCount}</div>

                {saving ? (
                    <CircularProgress/>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAssign}
                        disabled={staged.length === 0 || productCount === 0}
                        className="success-button-lg"
                    >
                        Assign to Selected
                    </Button>
                )}
            </div>
        </DrawerMenuRight>
    );
}

export default BulkAssignVehiclesDrawer;
